import React, { useState } from 'react';
import { Target, Pencil, Plus, Save } from 'lucide-react';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import Modal from 'src/shared/components/modal/Modal';
import useBoolean from 'src/shared/hooks/useBoolean';
import Textarea from 'src/shared/components/textarea/Textarea';
import Show from 'src/shared/components/show/Show';
import ErrorMessage from 'src/shared/components/error-msg/ErrorMessage';
import { useSetPortfolioObjectiveMutation } from 'src/app/portfolio/state/api/portfolioGraphSlice';
import usePortfolioAnalytics from 'src/app/portfolio/views/portfolio-summary/hooks/usePortfolioAnalytics';
import SummaryCard from './SummaryCard';

const ObjectiveCard = ({ portfolioObjective }: { portfolioObjective: string }) => {
    const [showEditObjectivePopup, setShowEditObjectivePopup] = useBoolean(false);
    const [objectiveText, setObjectiveText] = useState<string>(portfolioObjective || '');

    const emptyObjectiveCard = (
        <div className='flex items-center h-[168px]'>
            <div className='flex flex-col gap-2 text-center w-full'>
                <div className='text-2xl text-black'>Enter your portfolio objective</div>
                <div className='text-sm text-grey-80 mb-2'>This will be seen by anyone with access to this portfolio</div>
                <div className='w-auto self-center'>
                    <PrimaryButton className='flex gap-2' onClick={setShowEditObjectivePopup.on}>
                        Enter objective <Plus className='text-white' />
                    </PrimaryButton>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <SummaryCard
                dataTestId='portfolio-objective-card'
                containerClasses='gap-2 p-4'
                cardIcon={<Target className='text-primary' />}
                cardTitle='Objective'
                cardBody={
                    <Show when={objectiveText} fallback={emptyObjectiveCard}>
                        <div className='text-sm text-grey-80'>{objectiveText}</div>
                    </Show>
                }
                action={
                    objectiveText ? (
                        <SecondaryButton
                            className='flex items-center gap-2 p-2 h-[30px] border-none text-xs'
                            onClick={setShowEditObjectivePopup.on}
                        >
                            <Pencil className='text-primary' /> Edit
                        </SecondaryButton>
                    ) : null
                }
            />

            {showEditObjectivePopup && (
                <EditObjectiveModal
                    portfolioObjective={objectiveText}
                    onModalClose={textAreaValue => {
                        setShowEditObjectivePopup.off();
                        setObjectiveText(textAreaValue || '');
                    }}
                />
            )}
        </>
    );
};

const EditObjectiveModal = ({
    portfolioObjective,
    onModalClose,
}: {
    portfolioObjective: string;
    onModalClose: (textAreaValue: string) => void;
}) => {
    const [errorMessage, setErrorMessage] = React.useState('');
    const [textAreaValue, setTextAreaValue] = useState<string>(portfolioObjective || '');
    const objectiveDefaultValue = portfolioObjective || '';

    const { currentCustomerId } = useCustomer();
    const [setPortfolioObjective] = useSetPortfolioObjectiveMutation();

    const trackAnalytics = usePortfolioAnalytics();

    const submitForm = async () => {
        setErrorMessage('');
        try {
            const response = await setPortfolioObjective({
                customerId: currentCustomerId,
                objective: textAreaValue.trim(),
            }).unwrap();

            if (response) {
                onModalClose(textAreaValue.trim());
            }
        } catch (error) {
            console.error('rejected', error);
            setErrorMessage('There was an error saving the portfolio objective. Please try again.');
        } finally {
            trackAnalytics.trackEditObjectiveClick();
        }
    };

    return (
        <Modal
            classNames={{ body: 'w-[700px] p-6' }}
            onClose={() => {
                // Reset to default on close without saving
                onModalClose(objectiveDefaultValue);
            }}
            dismissOnClickOutside={true}
        >
            <div className='flex gap-3 flex-col'>
                <div className='flex gap-2 items-center'>
                    <div className='p-1 border border-primary rounded-full'>
                        <Target className='text-primary' />
                    </div>
                    <div className='text-xl text-primary'>Enter portfolio objective</div>
                </div>
                <div className='text-sm'>
                    Provide a description of the portfolio objectives. This will be seen by anyone with access to this portfolio.
                    <br />
                    <br />
                    You can edit this objective description at any time.
                </div>

                <Textarea
                    className='h-[200px]'
                    onChange={event => setTextAreaValue(event.target.value)}
                    value={textAreaValue}
                    maxLength={800}
                />

                <PrimaryButton
                    className='self-end gap-3'
                    onClick={() => {
                        submitForm();
                    }}
                    disabled={!textAreaValue.trim()}
                >
                    Save <Save className='text-white' />
                </PrimaryButton>

                {errorMessage && <ErrorMessage message={errorMessage} />}
            </div>
        </Modal>
    );
};

export default ObjectiveCard;
